/**
 * Physics configuration constants for bowling simulation
 * Using metric units (meters, kilograms, meters/second)
 */

// Ball Physics (Pool/Billiards)
export const BALL_MASS = 0.17; // kg (~170g, standard pool ball)
export const BALL_RADIUS = 0.086; // meters (86mm radius, 3x larger for visibility)
export const BALL_FRICTION = 0.2; // Pool table felt friction (realistic)
export const BALL_RESTITUTION = 0.92; // Realistic pool ball elasticity
export const BALL_MAX_VELOCITY = 1.5; // m/s (very gentle shots)
export const BALL_MIN_VELOCITY = 0.2; // m/s (minimal shot)

// Ball Spin Physics
export const BALL_SPIN_FACTOR = 0.008; // English/spin control
export const BALL_MAX_ANGULAR_VELOCITY = 2.0; // rad/s (moderate spin for english)
export const BALL_MAGNUS_FORCE = 0.01; // Magnus effect for curved shots
export const BALL_NATURAL_CURVE_FACTOR = 0.002; // Subtle natural curve

// Letter Ball Physics (formerly Pin Physics)
export const LETTER_BALL_MASS = 0.17; // kg (same as cue ball)
export const LETTER_BALL_RADIUS = 0.086; // meters (same size, 3x larger)
export const LETTER_BALL_FRICTION = 0.2; // Pool table felt friction (realistic)
export const LETTER_BALL_RESTITUTION = 0.92; // Realistic pool ball elasticity

// Table Physics (Pool Table)
export const TABLE_LENGTH = 5.0; // meters (~16.4 feet, longer table for better gameplay)
export const TABLE_WIDTH = 2.4; // meters (~7.9 feet, wider for pocket spacing)
export const TABLE_FRICTION = 0.2; // Pool felt

// Pocket Configuration
export const POCKET_RADIUS = 0.15; // meters (~15cm, slightly larger than 3x ball for easier sinking)

// Pocket size multipliers
export const POCKET_SIZE_MULTIPLIERS = {
  small: 0.8,   // 20% smaller
  medium: 1.0,  // baseline
  large: 1.25,  // 25% larger
} as const;

export const POCKET_POSITIONS = {
  BACK_LEFT: { x: -TABLE_WIDTH / 2, y: TABLE_LENGTH },
  BACK_CENTER: { x: 0, y: TABLE_LENGTH },
  BACK_RIGHT: { x: TABLE_WIDTH / 2, y: TABLE_LENGTH },
  SIDE_LEFT: { x: -TABLE_WIDTH / 2, y: TABLE_LENGTH - TABLE_WIDTH / 2 }, // Distance to back corner = distance between back corners
  SIDE_RIGHT: { x: TABLE_WIDTH / 2, y: TABLE_LENGTH - TABLE_WIDTH / 2 },
} as const;

// Simulation Settings
export const GRAVITY = 9.81; // m/s² (Earth gravity)
export const TIME_SCALE = 1.0; // Real-time simulation (1.0 = normal speed)
export const SIMULATION_STEPS_PER_FRAME = 6; // Physics substeps for accuracy

// Collision Groups (for Matter.js)
export const COLLISION_CATEGORY = {
  BALL: 0x0001,
  PIN: 0x0002,
  LANE: 0x0004,
  GUTTER: 0x0008,
} as const;

// Pin Knockdown Detection
export const PIN_KNOCKDOWN_ANGLE_THRESHOLD = Math.PI / 4; // 45 degrees tilt = knocked down
export const PIN_KNOCKDOWN_VELOCITY_THRESHOLD = 0.5; // m/s minimum velocity to count as knocked down

// Viewport/Rendering (for scaling physics to screen)
export const PIXELS_PER_METER = 50; // Scale factor for rendering (50 pixels = 1 meter)
